/**
 * DropOffReasonsPanel.tsx — WHY APPROVED PATIENTS HAVE NOT PAID, ranked (T-2,
 * 2026-09-11).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: admin Insights.
 *
 * The funnel plan's success test for T-2 is "enough responses to RANK the
 * reasons — a dominant reason is the signal". This panel is that ranking, in the
 * ADMIN Insights section's Company analytics view, read from clinic-service
 * `GET /v1/clinic/analytics/drop-off-reasons`: counts per reason and nothing
 * else (R19). The server zero-fills and ranks; this renders the order it is
 * given, so a second consumer can never rank the same counts differently.
 *
 * ITS OWN READ AND ITS OWN STATES. It sits beside the activity reads but does
 * not share their all-or-nothing load. Those three fail together by design; this
 * one must neither take them down nor be taken down by them — including in the
 * deploy window, when an older clinic-service answers this route with a 404.
 *
 * THE DENOMINATOR IS ON SCREEN. A ranking of three answers is not a signal, so
 * the number of patients who answered sits above the table and the reader
 * decides whether it is enough. Most patients who leave never say why; the copy
 * says this is the ones who did, rather than presenting it as every drop-off.
 *
 * The two clinical reasons carry "Clinician to call" in words — the same flag
 * the board card uses (pipeline/dropOffReasonsStaff.tsx).
 */
import { useCallback, useEffect, useState } from 'react';
import { getDropOffReasonCounts } from '../api/clinic';
import type { DropOffReasonCounts } from '../api/types';
import { DROP_OFF_REASON_STAFF_LABEL, isClinicalDropOffReason, isDropOffReasonKey } from '../pipeline/dropOffReasonsStaff';
import { SectionTitle } from '../staff/ui';
import { DataTable, Td, Th } from '../ui/Table';
import { Badge, Card, ErrorState, Help } from '../ui/kit';
import { TableSkeleton } from '../ui/skeletons';

const numberCell = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;

export function DropOffReasonsPanel({ days }: { days: number }) {
  const [data, setData] = useState<DropOffReasonCounts | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  const load = useCallback(() => {
    setStatus('loading');
    getDropOffReasonCounts(days)
      .then((counts) => {
        setData(counts);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Card>
      <SectionTitle>Why approved patients haven&rsquo;t paid</SectionTitle>
      {status === 'loading' ? (
        <TableSkeleton rows={4} columns={4} label="Loading drop-off reasons…" />
      ) : status === 'error' || !data ? (
        <ErrorState message="Sorry — we could not load why patients haven't paid." onRetry={load} />
      ) : (
        <RankedReasons data={data} />
      )}
    </Card>
  );
}

function RankedReasons({ data }: { data: DropOffReasonCounts }) {
  if (data.responses === 0) {
    return (
      <Help style={{ marginBottom: 0 }}>
        No approved patient has told us why they haven&rsquo;t paid in the last {data.windowDays} days. Most people who
        leave never say, so this fills in as they do.
      </Help>
    );
  }

  return (
    <>
      <p id="drop-off-reasons-scope" style={{ margin: '0 0 var(--gps-s-3)', fontSize: '13.5px', color: 'var(--gps-muted)' }}>
        {data.responses} {data.responses === 1 ? 'patient' : 'patients'} told us why in the last {data.windowDays} days,
        ranked by how many chose each reason. Most people who leave never say, so these are the ones who did.
        &ldquo;Still waiting to pay&rdquo; is as of today.
      </p>
      <DataTable
        label="Why approved patients haven't paid, ranked"
        head={
          <>
            <Th>Reason</Th>
            <Th style={{ textAlign: 'right' }}>Patients</Th>
            <Th style={{ textAlign: 'right' }}>Share</Th>
            <Th style={{ textAlign: 'right' }}>Still waiting to pay</Th>
          </>
        }
      >
        {data.reasons.map((row) => {
          const known = isDropOffReasonKey(row.reason);
          // A reason a newer server added renders as its raw key rather than vanishing.
          const label = known ? DROP_OFF_REASON_STAFF_LABEL[row.reason as keyof typeof DROP_OFF_REASON_STAFF_LABEL] : row.reason;
          const share = data.responses > 0 ? Math.round((row.count / data.responses) * 100) : 0;
          const muted = row.count === 0 ? { color: 'var(--gps-muted)' } : {};
          return (
            <tr key={row.reason}>
              <Td style={muted}>
                <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
                  <span>{label}</span>
                  {known && isClinicalDropOffReason(row.reason as keyof typeof DROP_OFF_REASON_STAFF_LABEL) ? (
                    <Badge tone="danger">Clinician to call</Badge>
                  ) : null}
                </span>
              </Td>
              <Td style={{ ...numberCell, ...muted }}>{row.count}</Td>
              <Td style={{ ...numberCell, ...muted }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end' }}>
                  {/* The bar repeats the percentage beside it, so it is hidden from assistive tech. */}
                  <span aria-hidden="true" style={shareTrack}>
                    <span style={{ ...shareFill, width: `${share}%` }} />
                  </span>
                  {share}%
                </span>
              </Td>
              <Td style={{ ...numberCell, ...muted }}>{row.unpaidNow}</Td>
            </tr>
          );
        })}
      </DataTable>
    </>
  );
}

const shareTrack = {
  display: 'inline-block',
  width: '64px',
  height: '6px',
  borderRadius: 'var(--gps-r-pill)',
  background: 'var(--gps-surface-2)',
  overflow: 'hidden',
} as const;

const shareFill = { display: 'block', height: '100%', background: 'var(--gps-primary)' } as const;
